import { useEffect, useRef } from 'react';

import { useConfigPodName } from '@lumahealthhq/ui-utils';
import { get } from 'lodash';

import { useCurrentMaster, useCurrentUser } from '@lib/hooks';

enum ScriptId {
  Blue = '0195636b-1cb8-742a-a6c6-4b91b201c958-test', // Publish to this version supported until: 08/25/2025
  Staging = '01956d43-ea4f-7d49-b917-2fb02925c78e-test', // Publish to this version supported until: 08/25/2025
  Production = '019543ee-d0ac-7b66-b3d2-1ad281d882d0', // Publish to this version supported until: 08/25/2025
}

const getScriptId = (podName: string, isDemoAccount: boolean) => {
  switch (podName) {
    case 'blue':
      return ScriptId.Blue;
    case 'staging':
      return ScriptId.Staging;
    case 'production':
      return `${ScriptId.Production}${isDemoAccount ? '-test' : ''}`;
    default:
      return '';
  }
};

/**
 * A React hook that injects OneTrust cookie consent scripts into the page.
 *
 * This hook handles the dynamic injection of OneTrust's cookie consent management scripts based on:
 * - Whether cookie consent is enabled in the master account settings
 * - The current pod environment (blue/staging/production)
 * - Whether the account is a demo account
 *
 * The hook will:
 * 1. Check if cookie consent is enabled in account settings
 * 2. Determine the appropriate OneTrust script ID based on environment
 * 3. Inject the OneTrust SDK script and wrapper function
 * 4. Clean up scripts on unmount
 *
 * @returns void
 */
export default function useInjectOneTrustScript() {
  const shouldInjectScript = useRef(true);
  const currentMaster = useCurrentMaster();
  const currentUser = useCurrentUser();
  const podName = useConfigPodName();

  useEffect(() => {
    if (!shouldInjectScript.current) {
      return;
    }

    const isCookieConsentEnabled: boolean = get(
      currentMaster || currentUser,
      'settings.patients.privacyPreferences.cookieConsent.enabled',
      false,
    );

    if (isCookieConsentEnabled) {
      const isDemoAccount = /demo\+[\w]+@lumahealth\.io/.test(currentMaster?.email ?? '');
      const SCRIPT_ID = getScriptId(podName, isDemoAccount);

      if (SCRIPT_ID) {
        // Create the external script tag
        const script = document.createElement('script');
        script.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
        script.type = 'text/javascript';
        script.charset = 'UTF-8';
        script.setAttribute('data-domain-script', SCRIPT_ID);

        // Append the external script to the head
        document.head.appendChild(script);

        // Create the inline script
        const inlineScript = document.createElement('script');
        inlineScript.type = 'text/javascript';
        inlineScript.innerHTML = 'function OptanonWrapper() { }';

        // Append the inline script to the head
        document.head.appendChild(inlineScript);

        shouldInjectScript.current = false;

        // Cleanup: Remove the scripts when component unmounts
        return () => {
          document.head.removeChild(script);
          document.head.removeChild(inlineScript);
        };
      }
    }
  }, [currentMaster, currentUser, podName]);
}
